import {
    EMPTY_REPORT_SETTING_VALUES,
    REPORT_SETTING_KEYS,
    ReportSettingKey,
    ReportSettingsStoredRow,
    StoredReportSettingValues,
    readReportSettingValues,
} from "@common/helpers/report-settings/report-setting-fields";
import {
    ReportSettingsTarget,
    toReportSettingsOwner,
} from "@common/helpers/report-settings/report-setting-candidates";
import { ReportSettingsSpot } from "@common/helpers/report-settings/explain-report-setting-spot";
import {
    SAVE_REPORT_SETTINGS_REPORT_KEYS,
    SaveReportSettingsChange,
} from "@common/schemas/domains/reportSettings/save-report-settings.schema";

export type ReportSettingsDraftFields = Partial<StoredReportSettingValues>;

export type UpdateReportSetting = <K extends ReportSettingKey>(
    key: K,
    value: StoredReportSettingValues[K],
) => void;

export type ReportSettingsDraftSpot = ReportSettingsSpot & {
    reportKey: (typeof SAVE_REPORT_SETTINGS_REPORT_KEYS)[number];
};

export interface ReportSettingsDraft {
    spot: ReportSettingsDraftSpot;
    fields: ReportSettingsDraftFields;
}

export type ReportSettingsDraftChange = Omit<
    SaveReportSettingsChange,
    "owner"
> & { owner: ReportSettingsTarget };

export const isSameTarget = (
    left: ReportSettingsTarget,
    right: ReportSettingsTarget,
): boolean => {
    switch (left.kind) {
        case "entity":
            return right.kind === "entity" && left.entityId === right.entityId;
        case "template":
            return (
                right.kind === "template" &&
                left.templateId === right.templateId
            );
        case "workspace":
            return right.kind === "workspace";
        default: {
            const exhaustiveCheck: never = left;
            return exhaustiveCheck;
        }
    }
};

export const isSameSpot = (
    left: ReportSettingsSpot,
    right: ReportSettingsSpot,
): boolean =>
    left.reportKey === right.reportKey &&
    isSameTarget(left.target, right.target);

const isRowAtSpot = (
    row: ReportSettingsStoredRow,
    { target, reportKey }: ReportSettingsSpot,
): boolean => row.reportKey === reportKey && isSameTarget(row.owner, target);

const assignField = <K extends ReportSettingKey>(
    fields: ReportSettingsDraftFields,
    key: K,
    value: StoredReportSettingValues[K],
): void => {
    fields[key] = value;
};

interface PickDifferingFieldsInput {
    fields: ReportSettingsDraftFields;
    baseline: ReportSettingsDraftFields;
}

const pickDifferingFields = ({
    fields,
    baseline,
}: PickDifferingFieldsInput): ReportSettingsDraftFields => {
    const differing: ReportSettingsDraftFields = {};
    for (const key of REPORT_SETTING_KEYS) {
        const value = fields[key];
        if (value === undefined || value === baseline[key]) {
            continue;
        }
        assignField(differing, key, value);
    }
    return differing;
};

interface SetDraftFieldInput<K extends ReportSettingKey> {
    drafts: ReportSettingsDraft[];
    spot: ReportSettingsDraftSpot;
    key: K;
    value: StoredReportSettingValues[K];
}

export const setDraftField = <K extends ReportSettingKey>({
    drafts,
    spot,
    key,
    value,
}: SetDraftFieldInput<K>): ReportSettingsDraft[] => {
    const current = drafts.find((draft) => isSameSpot(draft.spot, spot));
    const fields: ReportSettingsDraftFields = { ...current?.fields };
    assignField(fields, key, value);
    return current
        ? drafts.map((draft) => (draft === current ? { spot, fields } : draft))
        : [...drafts, { spot, fields }];
};

interface ClearSavedFieldsInput {
    drafts: ReportSettingsDraft[];
    savedDrafts: ReportSettingsDraft[];
}

export const clearSavedFields = ({
    drafts,
    savedDrafts,
}: ClearSavedFieldsInput): ReportSettingsDraft[] =>
    drafts.flatMap(({ spot, fields }) => {
        const saved = savedDrafts.find((draft) => isSameSpot(draft.spot, spot));
        const unsavedFields = pickDifferingFields({
            fields,
            baseline: saved?.fields ?? {},
        });
        return Object.keys(unsavedFields).length === 0
            ? []
            : [{ spot, fields: unsavedFields }];
    });

interface ApplyDraftsInput {
    workspaceId?: string;
    rows: ReportSettingsStoredRow[];
    drafts: ReportSettingsDraft[];
}

export const applyDrafts = ({
    workspaceId,
    rows,
    drafts,
}: ApplyDraftsInput): ReportSettingsStoredRow[] => {
    const touchedRows = rows.map((row) => {
        const matchingDrafts = drafts.filter((draft) =>
            isRowAtSpot(row, draft.spot),
        );
        return matchingDrafts.reduce(
            (merged, draft) => ({ ...merged, ...draft.fields }),
            row,
        );
    });
    const newRows = drafts
        .filter(({ spot }) => !rows.some((row) => isRowAtSpot(row, spot)))
        .map(({ spot, fields }): ReportSettingsStoredRow => ({
            ...EMPTY_REPORT_SETTING_VALUES,
            ...fields,
            owner: toReportSettingsOwner({ target: spot.target, workspaceId }),
            reportKey: spot.reportKey,
        }));
    return [...touchedRows, ...newRows];
};

const findStoredValues = (
    rows: ReportSettingsStoredRow[],
    spot: ReportSettingsSpot,
): StoredReportSettingValues => {
    const row = rows.find((candidate) => isRowAtSpot(candidate, spot));
    return row ? readReportSettingValues(row) : EMPTY_REPORT_SETTING_VALUES;
};

interface DraftsInput {
    rows: ReportSettingsStoredRow[];
    drafts: ReportSettingsDraft[];
}

export const buildSaveChanges = ({
    rows,
    drafts,
}: DraftsInput): ReportSettingsDraftChange[] =>
    drafts.flatMap(({ spot, fields }) => {
        const changedFields = pickDifferingFields({
            fields,
            baseline: findStoredValues(rows, spot),
        });
        if (Object.keys(changedFields).length === 0) {
            return [];
        }
        return [
            {
                owner: spot.target,
                reportKey: spot.reportKey,
                fields: changedFields,
            },
        ];
    });

const changedSpots = (input: DraftsInput): ReportSettingsSpot[] =>
    buildSaveChanges(input).map(({ owner, reportKey }) => ({
        target: owner,
        reportKey,
    }));

export const hasUnsavedEdits = (input: DraftsInput): boolean =>
    buildSaveChanges(input).length > 0;

export const hasUnsavedEditsForTarget = (
    input: DraftsInput & { target: ReportSettingsTarget },
): boolean =>
    changedSpots(input).some(({ target }) =>
        isSameTarget(target, input.target),
    );

export const hasUnsavedEditsForSpot = (
    input: DraftsInput & { spot: ReportSettingsSpot },
): boolean => changedSpots(input).some((spot) => isSameSpot(spot, input.spot));
